import { useQuery } from '@tanstack/react-query';
import { httpClient } from '@/utils/httpClient';
import { queryKeys } from '@/lib/queryKeys';
import { useAuth } from '@/hooks/useAuth';
import type { HierarchyEntity } from '@/types/hierarchy';

export interface HierarchyDataSource {
  endpoint: string;
  order?: string;
}

interface UseHierarchyEntitiesParams {
  dataSource: HierarchyDataSource;
  filters?: Record<string, string>;
  enabled?: boolean;
}

const buildSearchParams = (filters: Record<string, string> | undefined, order?: string) => {
  const params = new URLSearchParams();
  if (order) {
    params.set('order', order);
  }
  if (filters) {
    for (const [key, value] of Object.entries(filters)) {
      if (value === undefined || value === null || value === '') continue;
      params.set(key, value);
    }
  }
  return params;
};

export function useHierarchyEntities({
  dataSource,
  filters,
  enabled = true,
}: UseHierarchyEntitiesParams) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: queryKeys.hierarchy.entities(dataSource.endpoint, filters ?? null),
    queryFn: async () => {
      const params = buildSearchParams(filters, dataSource.order);
      const query = params.toString();
      const endpoint = query ? `${dataSource.endpoint}?${query}` : dataSource.endpoint;
      return await httpClient.getAll<HierarchyEntity>(endpoint);
    },
    staleTime: 30_000,
    enabled: enabled && isAuthenticated,
    meta: { dataSource },
  });
}
